import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Tag, BookOpen } from 'lucide-react';
import DocumentMeta from 'react-document-meta';
import BlogManager from '../../services/dashboard/BlogManager';
import { getBlogImageUrl } from '../../utils/mediaUrls';
import HomeLayout from '../../components/home/HomeLayout';

const BlogDetailPage = () => {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for "More Articles" section
  const [recentBlogs, setRecentBlogs] = useState([]);

  useEffect(() => {
    fetchBlogData();
    window.scrollTo(0, 0);
  }, [blogId]);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await BlogManager.getBlogById(blogId);
      setBlog(data);
      
      // Fetch active blogs to show in "More Articles"
      const allBlogs = await BlogManager.getActiveBlogs();
      if (allBlogs) {
        // Filter out current blog and take up to 3
        setRecentBlogs(allBlogs.filter(b => b.blogId.toString() !== blogId.toString()).slice(0, 3));
      }
    } catch (err) {
      console.error('Failed to fetch blog', err);
      setError('Article not found or is currently unavailable.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateArray) => {
    if (!dateArray) return "-";
    if (Array.isArray(dateArray)) {
      const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(dateArray).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog?.metaTitle || blog?.title,
          text: blog?.metaDescription || blog?.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <HomeLayout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 pb-12 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </HomeLayout>
    );
  }

  if (error || !blog) {
    return (
      <HomeLayout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 pb-12 flex flex-col justify-center items-center px-4">
          <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{error || 'Article not found'}</h2>
          <Link to="/blog" className="mt-4 text-orange-500 hover:text-orange-600 font-medium flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </div>
      </HomeLayout>
    );
  }

  // Dynamic SEO Metadata Configuration
  const meta = {
    title: blog.metaTitle || `${blog.title} | Books At Doorstep`,
    description: blog.metaDescription || blog.excerpt || blog.content?.replace(/<[^>]+>/g, '').substring(0, 160) + '...',
    canonical: window.location.href,
    meta: {
      charset: 'utf-8',
      name: {
        keywords: blog.metaKeywords || blog.tags || 'books, reading, rental',
      }
    }
  };

  const tagsList = blog.tags ? blog.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

  return (
    <DocumentMeta {...meta}>
      <HomeLayout>
        <article className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-12 pb-16">
          
          {/* Article Header */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-orange-500 hover:text-orange-600 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to all articles
          </Link>
          
          {blog.category && (
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-bold rounded-full">
                {blog.category}
              </span>
            </div>
          )}

          <h1 className="text-3xl sm:text-4xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 py-4 border-y border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{blog.author || 'Admin'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Author</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Calendar className="w-5 h-5" />
              <div className="text-sm">
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(blog.creationDate)}</p>
                <p className="text-xs">Published</p>
              </div>
            </div>

            <div className="ml-auto">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {blog.imageUrl && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="rounded-2xl overflow-hidden shadow-xl max-h-[480px]">
              <img
                src={getBlogImageUrl(blog.imageUrl)}
                alt={blog.title}
                className="w-full h-full object-cover object-center block"
              />
            </div>
          </div>
        )}

        {/* Article Body */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {blog.excerpt && (
            <p className="text-xl md:text-2xl font-medium text-gray-600 dark:text-gray-300 leading-relaxed mb-10 pb-10 border-b border-gray-200 dark:border-gray-700 italic">
              {blog.excerpt}
            </p>
          )}

          <div 
            className="blog-content-preview dark:text-gray-200"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Tags */}
          {tagsList.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mr-2">
                <Tag className="w-4 h-4" />
                <span className="font-medium">Tags:</span>
              </div>
              {tagsList.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* "More Articles" Section */}
        {recentBlogs.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 pt-8 border-t border-gray-200 dark:border-gray-800">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8">
              Read more articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recentBlogs.map((recentBlog) => (
                <Link 
                  key={recentBlog.blogId} 
                  to={`/blog/${recentBlog.blogId}`}
                  className="group flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700"
                >
                  {recentBlog.imageUrl && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getBlogImageUrl(recentBlog.imageUrl)}
                        alt={recentBlog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {recentBlog.category && (
                      <span className="text-xs font-bold text-orange-500 uppercase tracking-wider block mb-2">
                        {recentBlog.category}
                      </span>
                    )}
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-orange-500 transition-colors line-clamp-2 mb-3">
                      {recentBlog.title}
                    </h3>
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      {formatDate(recentBlog.creationDate)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        </article>
      </HomeLayout>
    </DocumentMeta>
  );
};

export default BlogDetailPage;
